import { useState, useEffect, useCallback } from 'react'
import { api } from '../../lib/api.js'
import styles from './MarksLibrary.module.css'

/**
 * Marks Library — the owned-first logo finder + propose→approve/reject queue.
 *  - Search a name → GET /api/hubspot/logo-find (owned File Manager first, then
 *    ESPN fallback; a write-behind proposal is ingested server-side).
 *  - Proposal queue → GET /api/hubspot/logo-proposals, POST logo-approve /
 *    logo-reject (approve promotes -proposed → -primary; reject → -rejected;
 *    never deletes).
 * `?demo=1` uses canned data so the flow is demoable without a session.
 */
function isDemo() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('demo') === '1'
}

const DEMO_RESULT = {
  found: true, source: 'espn', owned: false, matched: 'Northgate High School',
  confidence: 0.86, entityType: 'school', logo: '', needsManual: false,
}
const DEMO_PROPOSALS = [
  { id: 'p1', name: 'hs-school-northgate-high-3312-primary-proposed.png', url: '' },
  { id: 'p2', name: 'college-team-cedar-valley-8841-primary-proposed.png', url: '' },
]

export default function MarksLibrary({ prefillName }) {
  const demo = isDemo()
  const [name, setName] = useState(prefillName || '')
  const [entityType, setEntityType] = useState('school')
  const [sport, setSport] = useState('')
  const [result, setResult] = useState(null)
  const [findState, setFindState] = useState('idle') // idle|loading|done|error
  const [findErr, setFindErr] = useState('')

  const [proposals, setProposals] = useState([])
  const [propState, setPropState] = useState('loading')
  const [acting, setActing] = useState(null)

  useEffect(() => { if (prefillName) setName(prefillName) }, [prefillName])

  const loadProposals = useCallback(async () => {
    setPropState('loading')
    if (demo) { setProposals(DEMO_PROPOSALS); setPropState('done'); return }
    try {
      const res = await api.logoProposals()
      setProposals(res?.proposals || res?.files || res?.results || [])
      setPropState('done')
    } catch { setPropState('error') }
  }, [demo])

  useEffect(() => { loadProposals() }, [loadProposals])

  async function find(e) {
    e?.preventDefault()
    if (!name.trim()) return
    setFindState('loading'); setFindErr('')
    if (demo) { setResult({ ...DEMO_RESULT, matched: name }); setFindState('done'); return }
    try {
      const res = await api.logoFind({ name: name.trim(), entityType, sport, level: '' })
      setResult(res || null)
      setFindState('done')
    } catch (err) { setFindErr(err?.message || 'Lookup failed.'); setFindState('error') }
  }

  async function act(fileId, kind) {
    setActing(fileId)
    try {
      if (!demo) await (kind === 'approve' ? api.logoApprove(fileId) : api.logoReject(fileId))
      setProposals((p) => p.filter((x) => (x.id || x.fileId) !== fileId))
    } catch { /* keep row on failure */ } finally { setActing(null) }
  }

  return (
    <div className={styles.wrap}>
      {/* Finder */}
      <section className={styles.section}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Find a mark</div>
        <form className={styles.finderForm} onSubmit={find}>
          <input className="input" placeholder="School / team / league name…" value={name} onChange={(e) => setName(e.target.value)} />
          <select className="input" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            <option value="school">School</option>
            <option value="team">Team</option>
            <option value="league">League</option>
            <option value="conference">Conference</option>
            <option value="governing_body">Governing body</option>
          </select>
          <input className="input" placeholder="Sport (optional)" value={sport} onChange={(e) => setSport(e.target.value)} />
          <button className="btn btn-primary" type="submit" disabled={findState === 'loading' || !name.trim()}>
            {findState === 'loading' ? <span className="spinner" /> : 'Find'}
          </button>
        </form>

        {findState === 'error' && <p className={styles.err}>{findErr}</p>}
        {findState === 'done' && result && <FindResult result={result} />}
      </section>

      {/* Proposal queue */}
      <section className={styles.section}>
        <div className={styles.queueHead}>
          <div className="eyebrow">Proposal queue</div>
          <span className={styles.queueCount}>
            {propState === 'loading' ? 'Loading…' : `${proposals.length} pending`}
          </span>
        </div>
        <p className={styles.queueNote}>
          Owned copies staged from ESPN wait here. Approve promotes it to the live
          <code>-primary</code> mark; reject sets <code>-rejected</code> (reversible, never deleted).
        </p>
        {propState === 'error' ? (
          <p className={styles.err}>Couldn’t load proposals.</p>
        ) : proposals.length === 0 && propState === 'done' ? (
          <div className={styles.empty}>No proposals pending review.</div>
        ) : (
          <div className={styles.proposals}>
            {proposals.map((p) => {
              const id = p.id || p.fileId
              return (
                <div key={id} className={styles.proposal}>
                  <div className={styles.proposalThumb}>{p.url ? <img src={p.url} alt="" /> : <span>◈</span>}</div>
                  <div className={styles.proposalName} title={p.name}>{p.name || id}</div>
                  <div className={styles.proposalActions}>
                    <button className={styles.approve} disabled={acting === id} onClick={() => act(id, 'approve')}>Approve</button>
                    <button className={styles.reject} disabled={acting === id} onClick={() => act(id, 'reject')}>Reject</button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

function FindResult({ result }) {
  const owned = result.owned || result.source === 'owned'
  const found = result.found !== false && (result.logo || result.matched)
  if (result.needsManual || !found) {
    return (
      <div className={styles.findResult}>
        <div className={styles.manual}>No owned or ESPN mark found — this entity is staged for manual sourcing (never fabricated).</div>
      </div>
    )
  }
  return (
    <div className={styles.findResult}>
      <div className={styles.foundLogo}>{result.logo ? <img src={result.logo} alt="" /> : <span>◈</span>}</div>
      <div className={styles.foundBody}>
        <div className={styles.foundName}>{result.matched || 'Match'}</div>
        <div className={styles.foundMeta}>
          <span className={`${styles.sourceTag} ${owned ? styles.owned : ''}`}>{owned ? 'Owned' : (result.source || 'espn')}</span>
          {result.confidence != null && <span>{Math.round(result.confidence * 100)}% match</span>}
          {result.entityType && <span>{result.entityType}</span>}
        </div>
        {!owned && <div className={styles.foundNote}>An owned copy is being staged to the proposal queue for review.</div>}
      </div>
    </div>
  )
}
